import { dedent } from 'ts-dedent'
import { DataWarehouseConnection } from '..'
import { Workflow } from '../..'
import { WorkflowComponent } from '../../../components/common/workflowcomponent'
import { ReadTable } from '../../../components/readtable'
import {
  ColumnDataType,
  ExecuteDynamicSQLParams,
  LocationType,
  NumericType,
  Provider,
  StoredProcedureScope,
  TableOptimizationColumnDataType,
  TableOptimizationInfo,
  WorkflowCodeType,
  WorkflowComponentParamType,
  WorkflowComponentValue,
  WorkflowEnvVars,
  WorkflowTableSchemas,
  WorkflowVariable
} from '../../../types'
import {
  OUTPUT_TABLE_FQN_VAR_NAME,
  OUTPUT_TABLE_NAME_VAR_NAME,
  WORKFLOW_OUTPUT_TABLE_NAME_PARAMETER_NAME,
  formatVariableValue,
  hashString
} from '../../../utils'

export class RSConnection extends DataWarehouseConnection {
  constructor(name: string, tempLocation: string, workspaceLocation: string, extensionsLocation: string) {
    super(name, tempLocation, workspaceLocation, extensionsLocation, Provider.Redshift)
  }

  castValue(value: string, type: string) {
    return `(${value})::${type}`
  }

  getTempSelectQuery(select: string, dryRun: boolean): string {
    if (dryRun) {
      return dedent`SELECT * FROM
                (
                  ${select}
                )
              WHERE 1=0`
    } else {
      return select
    }
  }

  getCreateTempTableQuery(
    tablename: string,
    select: string,
    codeType: WorkflowCodeType,
    optimizeBy: TableOptimizationInfo | null,
    addOptimizationStats?: boolean
  ): string {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const temp = codeType === WorkflowCodeType.StoredProcedure ? 'TEMPORARY ' : ''
    select = dedent(select)
    tablename = this.quoteName(tablename)
    if (!dryRun && optimizeBy?.column && TableOptimizationColumnDataType.Geo !== optimizeBy.column.type) {
      return dedent`
                DROP TABLE IF EXISTS ${tablename} CASCADE;
                CREATE ${temp}TABLE ${tablename}
                AS
                  ${this.getTempSelectQuery(select, dryRun)};
                ALTER TABLE ${tablename} ALTER SORTKEY
                  (
                    ${optimizeBy.column.name}
                  );`
    } else {
      return dedent`
              DROP TABLE IF EXISTS ${tablename} CASCADE;
              CREATE ${temp}TABLE ${tablename}
                AS
                  ${this.getTempSelectQuery(select, dryRun)};`
    }
  }

  getCreateTableQuery(
    tablename: string,
    select: string,
    codeType: WorkflowCodeType,
    optimizeBy: TableOptimizationInfo | null,
    addOptimizationStats?: boolean
  ): string {
    return this.getCreateTempTableQuery(tablename, select, codeType, optimizeBy, addOptimizationStats).replace(
      RegExp('TEMPORARY ', 'g'),
      ''
    )
  }

  getDropTableQuery(tablename: string): string {
    return `DROP TABLE IF EXISTS ${this.quoteName(tablename)} CASCADE;`
  }

  getOutputTableName(
    workflowId: string,
    componentName: string,
    params: Record<string, unknown>,
    output: string,
    codeType: WorkflowCodeType
  ): string {
    return super.getOutputTableName(workflowId, componentName, params, output, codeType).toLowerCase()
  }

  wrapComponentCodeInFlowControlBlock(query: string, codeType: WorkflowCodeType): string {
    return dedent`BEGIN
              ${query}
            END;`
  }

  getStoredProcedureOutputTableNameExpression(workflow: Workflow, full: boolean, scope: StoredProcedureScope): string {
    const publicVars = workflow.variables.filter((v) => v.scopes?.includes(scope))
    let root = workflow.getStoredProcedureOutputTableFullName(this, scope)
    if (!full) {
      root = root.split('.').pop() as string
    }
    if (publicVars.length === 0) {
      return `'${root}_' || SUBSTRING(MD5(''), 1, 16)`
    } else {
      return `'${root}_' || SUBSTRING(MD5(${publicVars
        .map((v) => this.quoteVariableName(v.name))
        .join(" || ',' || ")}), 1, 16)`
    }
  }

  wrapCodeInIfTableExistsBlock(
    query: string,
    outputTables: Array<string>,
    codeType: WorkflowCodeType,
    outputTablesExpressions?: Array<string>,
    variablePrefix?: string
  ): string {
    variablePrefix = variablePrefix || ''
    if ([WorkflowCodeType.DryRun, WorkflowCodeType.StoredProcedure].includes(codeType)) {
      return query
    } else if (codeType === WorkflowCodeType.FullNoCache) {
      return dedent`
        ${outputTables.map((x) => `DROP TABLE IF EXISTS ${this.quoteName(x)} CASCADE;`).join('\n')}
        ${query}
      `
    } else {
      const [database, schema] = this.tempLocation.toLowerCase().split('.')
      const expressionChecks: Array<string> = []
      for (const expression of outputTablesExpressions || []) {
        expressionChecks.push(dedent`
            ${variablePrefix}schema := SPLIT_PART(${expression}, '.', 2);
            ${variablePrefix}tableName := SPLIT_PART(${expression}, '.', 3);
            EXECUTE
              'SELECT
                COUNT(1) AS cnt
              FROM
                "${database}".information_schema.tables
              WHERE
                  table_name LIKE ''' || ${variablePrefix}tableName || '''
                  AND table_schema LIKE ''' || ${variablePrefix}schema || ''''
              INTO ${variablePrefix}tablesCount;
            ${variablePrefix}totalTablesCount := ${variablePrefix}tablesCount + ${variablePrefix}totalTablesCount;
        `)
      }
      const outputTablesLiteralNames = outputTables.map((x) => `''${(x.split('.').pop() as string).toLowerCase()}''`)
      let literalNamesCheck = ''
      if (outputTablesLiteralNames.length > 0) {
        literalNamesCheck = dedent`
          ${variablePrefix}tablesNames := ' ${outputTablesLiteralNames.join(',')} ' ;
          ${variablePrefix}query = '
            SELECT COUNT(1) AS cnt
            FROM "${database}".information_schema.tables
            WHERE
              table_name IN (' || ${variablePrefix}tablesNames || ')
              AND table_schema LIKE ''${schema}''
          ';
          EXECUTE ${variablePrefix}query INTO ${variablePrefix}tablesCount;
          ${variablePrefix}totalTablesCount := ${variablePrefix}tablesCount + ${variablePrefix}totalTablesCount;
        `
      }
      const totalTablesCount = outputTables.length + (outputTablesExpressions?.length || 0)
      return dedent`
          DECLARE
            ${variablePrefix}query VARCHAR(MAX);
            ${variablePrefix}tablesCount INTEGER;
            ${variablePrefix}totalTablesCount INTEGER DEFAULT 0;
            ${variablePrefix}tablesNames VARCHAR(MAX);
            ${variablePrefix}schema VARCHAR(MAX);
            ${variablePrefix}tableName VARCHAR(MAX);
          BEGIN
            ${expressionChecks.join('\n')}
            ${literalNamesCheck}
            IF (${variablePrefix}totalTablesCount < ${totalTablesCount}) THEN
              BEGIN
                ${query}
              END;
            END IF;
          END;`
    }
  }

  async getWorkflowCode(
    workflow: Workflow,
    envVars: WorkflowEnvVars,
    codeType: WorkflowCodeType,
    schemas?: WorkflowTableSchemas,
    preQuery?: string
  ): Promise<string> {
    const queries = await workflow.getSubqueries(this, envVars, codeType, schemas)
    const executable = queries.some((q) => q.query)
    let query = ''
    if (executable) {
      const procName = `_temp_${hashString(JSON.stringify(workflow.componentValues))}`
      if (codeType !== WorkflowCodeType.StoredProcedure) {
        query = await this._getScriptBody(workflow, envVars, queries, codeType, schemas)
        query = dedent`
                /*
                ${workflow.workflowMetadataAsCommentString(codeType)}
                */
                CREATE OR REPLACE PROCEDURE ${procName}()
                AS $$
                BEGIN
                  ${preQuery || ''}${query.trim()}
                END;
                $$ LANGUAGE plpgsql;

                CALL ${procName}();
                DROP PROCEDURE ${procName}();`
        query = this.replaceVariablesInScriptQuery(query, workflow.variables)
      } else {
        query = await this._getProcedureBody(workflow, envVars, queries, schemas)
      }
    }

    return query
  }

  replaceVariablesInStoredProcedureQuery(
    variables: Array<WorkflowVariable>,
    query: string,
    requiresDynamicSQL: boolean
  ) {
    if (!query || !Array.isArray(variables) || variables.length === 0) {
      return query || ''
    }

    // Sort by name length (descending) to avoid substring replacement issues
    const sortedVariables = [...variables].sort((a, b) => b.name.length - a.name.length)

    if (sortedVariables.length === 0) {
      return query
    }

    if (requiresDynamicSQL) {
      query = query.replace(/'/g, "\\'").replace(/BEGIN\n([\s\S]*?)END;/, '$1')
      query = this.executeDynamicSQL({ sql: query, addQuotes: true })
    }

    for (const variable of sortedVariables) {
      // Replace the following patterns with the following priority:
      // 1. Pattern for "SELECT * FROM @variable"
      // 2. Pattern for variable inside JSON strings
      // 3. Pattern for generic variable values

      if (requiresDynamicSQL) {
        const replacement = `' || ${this.quoteVariableName(variable.name)} || '`
        query = query
          .replace(new RegExp(`SELECT\\s+\\*\\s+FROM\\s+@${variable.name}`, 'gi'), `SELECT * FROM ${replacement}`)
          .replace(new RegExp(`"@${variable.name}"`, 'g'), `"${replacement}"`)
          .replace(
            new RegExp(`@${variable.name}`, 'g'),
            variable.type === WorkflowComponentParamType.String ? `\\'${replacement}\\'` : replacement
          )
      } else {
        const replacement = this.quoteVariableName(variable.name)
        query = query
          .replace(new RegExp(`SELECT\\s+\\*\\s+FROM\\s+@${variable.name}`, 'gi'), `SELECT * FROM ${replacement}`)
          .replace(new RegExp(`"@${variable.name}"`, 'g'), `"' || ${replacement} || '"`)
          .replace(new RegExp(`@${variable.name}`, 'g'), replacement)
      }
    }

    return query
  }

  paramTypeToDataProviderType(inputType: WorkflowComponentParamType) {
    switch (inputType) {
      case WorkflowComponentParamType.String:
        return 'VARCHAR(MAX)'
      case WorkflowComponentParamType.Number:
        return 'FLOAT'
      case WorkflowComponentParamType.Boolean:
        return 'BOOLEAN'
      default:
        return 'VARCHAR(MAX)'
    }
  }

  columnTypeToDataProviderType(colType: ColumnDataType) {
    switch (colType) {
      case ColumnDataType.String:
        return 'VARCHAR(MAX)'
      case ColumnDataType.Number:
        return 'FLOAT'
      case ColumnDataType.Boolean:
        return 'BOOLEAN'
      case ColumnDataType.Geometry:
        return 'GEOMETRY'
      case ColumnDataType.Binary:
        return 'VARBYTE'
      default:
        return 'VARCHAR(MAX)'
    }
  }

  numericTypeToDataProviderType(numericType: NumericType) {
    return numericType === NumericType.Float ? 'FLOAT' : 'INT'
  }

  wrapCodeInProcedure(
    name: string,
    workflow: Workflow,
    body: string,
    includeDiagram: boolean,
    isImmutable: boolean,
    scope: StoredProcedureScope
  ): string {
    const hasOutput = workflow.hasStoredProcedureOutputTable(scope)
    const procedureParams = workflow.variables
      .filter((v) => v.scopes?.includes(scope))
      .map((input) => `${this.quoteVariableName(input.name)} ${this.paramTypeToDataProviderType(input.type)}`)
    if (hasOutput) {
      procedureParams.push(`${WORKFLOW_OUTPUT_TABLE_NAME_PARAMETER_NAME} INOUT VARCHAR(MAX)`)
    }
    const declarePrivateVariables = workflow.variables
      .filter((v) => !v.scopes?.includes(scope))
      .map(
        (input) =>
          `${this.quoteVariableName(input.name)} ${this.paramTypeToDataProviderType(
            input.type
          )} DEFAULT ${formatVariableValue(input)};`
      )
    const declarePublicVariables = workflow.variables
      .filter((v) => v.scopes?.includes(scope))
      .map(
        (input) =>
          `__${input.name} ${this.paramTypeToDataProviderType(input.type)} DEFAULT COALESCE(${this.quoteVariableName(
            input.name
          )}, ${formatVariableValue(input)});`
      )
    const declareVariables = declarePrivateVariables.concat(declarePublicVariables)
    let outputTableVars = ''
    if (hasOutput || isImmutable) {
      declareVariables.push(`${OUTPUT_TABLE_NAME_VAR_NAME} VARCHAR(MAX);`)
      declareVariables.push(`${OUTPUT_TABLE_FQN_VAR_NAME} VARCHAR(MAX);`)
      const outputTableNameVariable = `${OUTPUT_TABLE_NAME_VAR_NAME} := ${this.getStoredProcedureOutputTableNameExpression(
        workflow,
        false,
        scope
      )};`
      const outputTableFqnVariable = `${OUTPUT_TABLE_FQN_VAR_NAME} := ${this.getStoredProcedureOutputTableNameExpression(
        workflow,
        true,
        scope
      )};`
      outputTableVars = dedent`${outputTableNameVariable}
                               ${outputTableFqnVariable}`
    }
    const comment = this._getStoredProcedureEmbeddedComment(workflow)
    const returnTableName = hasOutput
      ? dedent`
    ${WORKFLOW_OUTPUT_TABLE_NAME_PARAMETER_NAME} := ${OUTPUT_TABLE_FQN_VAR_NAME};`
      : ''
    return dedent`
                CREATE OR REPLACE PROCEDURE
                  ${this.workspaceLocation}.${name}(
                    ${procedureParams.join(',\n')}
                  )
                  LANGUAGE plpgsql
                  AS
                  $$
                  ${includeDiagram ? comment : ''}
                  ${
                    declareVariables.length > 0
                      ? dedent`
                        DECLARE
                          ${declareVariables.join('\n')}`
                      : ''
                  }
                  BEGIN
                    ${outputTableVars}
                    ${body}
                    ${returnTableName}
                  END;
                  $$`
  }

  getRemoveStoredProcedureQueries(name: string): Array<string> {
    const [, schema, procedureName] = name.split('.')
    return [
      dedent`
      CREATE OR REPLACE PROCEDURE ${schema}.__DROP_FUNCTIONS
      (schema_name VARCHAR(MAX), procedure_name VARCHAR(MAX))
      AS $$
      DECLARE
        row RECORD;
      BEGIN
        DROP TABLE IF EXISTS _udfs_info;

        CREATE TEMP TABLE _udfs_info (f_oid BIGINT, f_kind VARCHAR(1), f_name VARCHAR(MAX), arg_index BIGINT, f_argtype VARCHAR(MAX));
        FOR row IN SELECT oid::BIGINT f_oid, kind::VARCHAR(1) f_kind, proname::VARCHAR(MAX) f_name, i arg_index, format_type(arg_types[i-1], null)::VARCHAR(MAX) f_argtype
          FROM (
            SELECT oid, kind, proname, generate_series(1, arg_count) AS i, arg_types
            FROM (
              SELECT p.prooid oid, p.prokind kind, proname, proargtypes arg_types, pronargs arg_count
              FROM pg_catalog.pg_namespace n
              JOIN PG_PROC_INFO p
              ON  pronamespace = n.oid
              WHERE nspname = schema_name
              and p.proname = procedure_name
            ) t
          ) t
        LOOP
          INSERT INTO _udfs_info(f_oid, f_kind, f_name,arg_index,f_argtype) VALUES (row.f_oid, row.f_kind, row.f_name, row.arg_index, row.f_argtype);
        END LOOP;

        FOR row IN SELECT drop_command
          FROM (
              SELECT 'DROP ' || CASE f_kind WHEN 'p' THEN 'PROCEDURE' ELSE 'FUNCTION' END || ' ' || schema_name || '.' || f_name || '(' || listagg(f_argtype,',' ) WITHIN GROUP (ORDER BY arg_index) || ');' AS drop_command
              FROM _udfs_info
              GROUP BY f_oid, f_name, f_kind
            )
          LOOP
          execute row.drop_command;
        END LOOP;

      END;
      $$ LANGUAGE plpgsql;
      `,
      `CALL ${schema}.__DROP_FUNCTIONS('${schema}', '${procedureName}')`
    ]
  }

  extractErrorLineNumberFromApiResponse(response: string): number | null {
    const match = response.match(/line (\d+)/)
    if (match) {
      const OFFSET = 7 // offset caused by header comment and procedure definition
      return parseInt(match[1]) + OFFSET
    } else {
      return null
    }
  }

  getSourceTablePermissionsQuery(workflow: Workflow) {
    const conditions: Record<string, Array<string>> = {}
    const tablenames: Array<string> = []
    for (const [componentName, component] of Object.entries(workflow.components)) {
      if (component.metadata.name === new ReadTable().metadata.name) {
        const value = workflow.componentValues[componentName]['source']
        const tablename = (value as WorkflowComponentValue).value as string
        tablenames.push(tablename.replace(/"/g, ''))
        const [catalog, schema, name] = tablename.toLowerCase().split('.')
        if (!(catalog in conditions)) {
          conditions[catalog] = []
        }
        conditions[catalog].push(
          dedent`(schemaname = '${schema}'
            AND tablename = '${name}'
            AND HAS_TABLE_PRIVILEGE(CURRENT_USER, table_name, 'select'))`
        )
        tablename
      }
    }
    if (tablenames.length === 0) {
      return ''
    }
    const accessible = Object.keys(conditions)
      .map((k) => {
        return dedent`SELECT CURRENT_DATABASE() || '.' || schemaname || '.' || tablename AS table_name
                FROM pg_tables
                WHERE ${conditions[k].join('\nOR\n')}`
      })
      .join('\nUNION ALL\n')

    const query = dedent`WITH _accessible AS (
              ${accessible}
            ),
            _names AS (
              ${tablenames.map((x) => `SELECT '${x}'::varchar AS name`).join('\nUNION ALL\n')}
            )
            SELECT name AS noAccess
            FROM _names n
            WHERE NOT EXISTS (
              SELECT 1
              FROM _accessible a
              WHERE n.name = a.table_name
            );`
    return query
  }

  getStorageSetupQueries(options?: any) {
    const cartoTempSchema = this.tempLocation.split('.').pop()
    const cartoWorkspaceSchema = this.workspaceLocation.split('.').pop()
    const cartoExtensionsSchema = this.extensionsLocation.split('.').pop()
    const procName = `_temp_proc_${hashString(this.tempLocation)}`
    const proc = dedent`
      DECLARE
        schemaExists BOOL;
        messageCartoTemp VARCHAR(MAX);
        messageCartoWorkspace VARCHAR(MAX);
        messageCartoExtensions VARCHAR(MAX);
      BEGIN
        SELECT COUNT(*) != 0 INTO schemaExists FROM pg_namespace WHERE nspname = '${cartoTempSchema}';
        IF (schemaExists) THEN
          BEGIN
            EXECUTE 'CREATE TABLE ${cartoTempSchema}.__testtable AS (SELECT 1 AS N);';
            EXECUTE 'DROP TABLE IF EXISTS ${cartoTempSchema}.__testtable;';
            messageCartoTemp := '{"success": true, "location": "carto_temp"}';
          EXCEPTION WHEN OTHERS THEN
            messageCartoTemp := '{"success": false, "error": "temp_storage_create_tables", "location": "carto_temp"}';
          END;
        ELSE
          BEGIN
            CREATE SCHEMA IF NOT EXISTS ${cartoTempSchema};
            messageCartoTemp := '{"success": true, "location": "carto_temp"}';
          EXCEPTION WHEN OTHERS THEN
            messageCartoTemp := '{"success": false, "error": "temp_storage_access_create", "location": "carto_temp"}';
          END;
        END IF;
        SELECT COUNT(*) != 0 INTO schemaExists FROM pg_namespace WHERE nspname = '${cartoWorkspaceSchema}';
        IF (schemaExists) THEN
          BEGIN
            EXECUTE 'CREATE TABLE ${cartoWorkspaceSchema}.__testtable AS (SELECT 1 AS N);';
            EXECUTE 'DROP TABLE IF EXISTS ${cartoWorkspaceSchema}.__testtable;';
            messageCartoWorkspace := '{"success": true, "location": "carto_workspace"}';
          EXCEPTION WHEN OTHERS THEN
            messageCartoWorkspace := '{"success": false, "error": "temp_storage_create_tables", "location": "carto_workspace"}';
          END;
        ELSE
          BEGIN
            CREATE SCHEMA IF NOT EXISTS ${cartoWorkspaceSchema};
            messageCartoWorkspace := '{"success": true, "location": "carto_workspace"}';
          EXCEPTION WHEN OTHERS THEN
            messageCartoWorkspace := '{"success": false, "error": "temp_storage_access_create", "location": "carto_workspace"}';
          END;
        END IF;
        SELECT COUNT(*) != 0 INTO schemaExists FROM pg_namespace WHERE nspname = '${cartoExtensionsSchema}';
        IF (schemaExists) THEN
          BEGIN
            EXECUTE 'CREATE TABLE ${cartoExtensionsSchema}.__testtable AS (SELECT 1 AS N);';
            EXECUTE 'DROP TABLE IF EXISTS ${cartoExtensionsSchema}.__testtable;';
            messageCartoExtensions := '{"success": true, "location": "carto_extensions"}';
          EXCEPTION WHEN OTHERS THEN
            messageCartoExtensions := '{"success": false, "error": "temp_storage_create_tables", "location": "carto_extensions"}';
          END;
        ELSE
          BEGIN
            CREATE SCHEMA IF NOT EXISTS ${cartoExtensionsSchema};
            messageCartoExtensions := '{"success": true, "location": "carto_extensions"}';
          EXCEPTION WHEN OTHERS THEN
            messageCartoExtensions := '{"success": false, "error": "temp_storage_access_create", "location": "carto_extensions"}';
          END;
        END IF;
        SELECT '[' || messageCartoTemp || ', ' || messageCartoWorkspace || ', ' || messageCartoExtensions || ']' INTO result;
      END;`

    // Redshift procedures allow OUT parameters
    return [
      dedent`CREATE OR REPLACE PROCEDURE ${procName}(result OUT VARCHAR(MAX))
       AS $$
         ${proc}
       $$ LANGUAGE plpgsql;`,
      `CALL ${procName}();`,
      `DROP PROCEDURE ${procName}();`
    ]
  }

  getStoredProcedureCacheInvalidationQueries(name: string): Array<string> {
    const tablePrefix = this.getStoredProcedureOutputTableNameForProcedureName(name.split('.').pop() as string)
    const procName = `_invalidate_stored_proc_cache_${hashString(this.workspaceLocation)}`
    const proc = dedent`
      DECLARE
        statement RECORD;
        query VARCHAR(MAX);
      BEGIN
        query := 'SELECT
            ''DROP TABLE ${this.workspaceLocation}.'' || relname || '';'' as statement
          FROM
            pg_class_info
          LEFT JOIN
            pg_namespace
          ON
            pg_class_info.relnamespace = pg_namespace.oid
          WHERE
            reltype != 0
            AND relname ILIKE ''${tablePrefix}%''
        ';
        FOR statement IN EXECUTE query
        LOOP
          EXECUTE statement.statement;
        END LOOP;
      END;`
    return [
      dedent`CREATE OR REPLACE PROCEDURE ${procName}()
       AS $$
         ${proc}
       $$ LANGUAGE plpgsql;`,
      `CALL ${procName}();`,
      `DROP PROCEDURE ${procName}();`
    ]
  }

  getTableListQuery(filter: string, locationType: LocationType = LocationType.Temp): string | null {
    const [database, schema] = this.getLocation(locationType).split('.')
    filter = this.applyCapitalization(filter)
    return dedent`
      SELECT table_name
      FROM "${database}".information_schema.tables
      WHERE table_schema = '${schema}' AND table_name LIKE '${filter}';`
  }

  dataProviderTypeToColumnType(type: string): ColumnDataType {
    switch (type) {
      case 'INT2':
      case 'INT4':
      case 'INT8':
      case 'FLOAT4':
      case 'FLOAT8':
      case 'NUMERIC':
        return ColumnDataType.Number
      case 'BOOL':
        return ColumnDataType.Boolean
      case 'GEOMETRY':
        return ColumnDataType.Geometry
      case 'GEOGRAPHY':
        return ColumnDataType.Geography
      case 'DATE':
        return ColumnDataType.Date
      case 'TIME':
      case 'TIMETZ':
        return ColumnDataType.Time
      case 'TIMESTAMP':
      case 'TIMESTAMPTZ':
        return ColumnDataType.Timestamp
      case 'VARBYTE':
        return ColumnDataType.Binary
      default:
        return ColumnDataType.String
    }
  }

  quoteName(name: string) {
    if (name) {
      const parts = name.split('.')
      if (parts.length === 3) {
        name = `"${parts[0].replace(/"/g, '')}".${parts[1]}.${parts[2]}`
      }
    }
    return name
  }

  executeDynamicSQL({ sql, addQuotes, replacements }: ExecuteDynamicSQLParams) {
    const lines = sql
      .replace(/BEGIN\n([\s\S]*?)END;/, '$1')
      .split(';')
      .filter((s) => /\S/.test(s))
    if (replacements) {
      return lines
        .map(
          (q) => dedent`EXECUTE
                          ${replacements?.reduce(
                            (acc, [org, repl]) =>
                              dedent`REPLACE(
                                ${acc},
                                ${org},
                                ${repl}
                              )
                              `,
                            addQuotes ? this.quoteMultilineString(dedent(q)) : dedent(q)
                          )};`
        )
        .join('\n')
    } else {
      return lines
        .map(
          (q) => dedent`EXECUTE
                          ${addQuotes ? this.quoteMultilineString(dedent(q)) : dedent(q)}
                        ;`
        )
        .join('\n')
    }
  }

  applyCapitalization(tablename: string): string {
    return tablename.toLowerCase()
  }

  adaptComponent(component: WorkflowComponent) {
    const whiteList = ['native.geographytogeometry']
    if (!whiteList.includes(component.metadata.name)) {
      for (const input of component.inputs) {
        if (input.type === WorkflowComponentParamType.Column && input.dataType) {
          input.dataType = (input.dataType as Array<ColumnDataType>).filter((x) => x !== ColumnDataType.Geography)
        }
      }
    }
    component.inputs = component.inputs.filter((x) => x.providers == null || x.providers.includes(Provider.Redshift))
  }

  getOutputSchemasQueries(tableNames: Array<string>): string {
    const parts = this.tempLocation.split('.')
    const schema = parts[1]
    return dedent`
      WITH cols AS (
        SELECT
          table_name,
          column_name AS name,
          data_type,
          ordinal_position
        FROM svv_columns
        WHERE
          table_name IN (${tableNames.map((t) => `'${t}'`).join(', ')})
          AND table_schema = '${schema}'
      ),
      cols_with_types AS (
        SELECT
          *,
          CASE LOWER(data_type)
            WHEN 'smallint' THEN 'int2'
            WHEN 'int2' THEN 'int2'
            WHEN 'integer' THEN 'int4'
            WHEN 'int' THEN 'int4'
            WHEN 'int4' THEN 'int4'
            WHEN 'bigint' THEN 'int8'
            WHEN 'int8' THEN 'int8'
            WHEN 'real' THEN 'float4'
            WHEN 'float4' THEN 'float4'
            WHEN 'double precision' THEN 'float8'
            WHEN 'float' THEN 'float8'
            WHEN 'float8' THEN 'float8'
            WHEN 'decimal' THEN 'numeric'
            WHEN 'numeric' THEN 'numeric'
            WHEN 'boolean' THEN 'bool'
            WHEN 'bool' THEN 'bool'
            WHEN 'geometry' THEN 'geometry'
            WHEN 'geography' THEN 'geography'
            WHEN 'date' THEN 'date'
            WHEN 'time' THEN 'time'
            WHEN 'time without time zone' THEN 'time'
            WHEN 'timetz' THEN 'timetz'
            WHEN 'time with time zone' THEN 'timetz'
            WHEN 'timestamp' THEN 'timestamp'
            WHEN 'timestamp without time zone' THEN 'timestamp'
            WHEN 'timestamptz' THEN 'timestamptz'
            WHEN 'timestamp with time zone' THEN 'timestamptz'
            WHEN 'character' THEN 'char'
            WHEN 'nchar' THEN 'char'
            WHEN 'bpchar' THEN 'char'
            WHEN 'char' THEN 'char'
            WHEN 'character varying' THEN 'varchar'
            WHEN 'nvarchar' THEN 'varchar'
            WHEN 'text' THEN 'varchar'
            WHEN 'varchar' THEN 'varchar'
            WHEN 'varbinary' THEN 'varbyte'
            WHEN 'binary varying' THEN 'varbyte'
            WHEN 'varbyte' THEN 'varbyte'
            ELSE data_type
          END AS db_type,
          CASE LOWER(data_type)
            WHEN 'smallint' THEN '${ColumnDataType.Number}'
            WHEN 'int2' THEN '${ColumnDataType.Number}'
            WHEN 'integer' THEN '${ColumnDataType.Number}'
            WHEN 'int' THEN '${ColumnDataType.Number}'
            WHEN 'int4' THEN '${ColumnDataType.Number}'
            WHEN 'bigint' THEN '${ColumnDataType.Number}'
            WHEN 'int8' THEN '${ColumnDataType.Number}'
            WHEN 'real' THEN '${ColumnDataType.Number}'
            WHEN 'float4' THEN '${ColumnDataType.Number}'
            WHEN 'double precision' THEN '${ColumnDataType.Number}'
            WHEN 'float' THEN '${ColumnDataType.Number}'
            WHEN 'float8' THEN '${ColumnDataType.Number}'
            WHEN 'decimal' THEN '${ColumnDataType.Number}'
            WHEN 'numeric' THEN '${ColumnDataType.Number}'
            WHEN 'boolean' THEN '${ColumnDataType.Boolean}'
            WHEN 'bool' THEN '${ColumnDataType.Boolean}'
            WHEN 'geometry' THEN '${ColumnDataType.Geometry}'
            WHEN 'geography' THEN '${ColumnDataType.Geography}'
            WHEN 'date' THEN '${ColumnDataType.Date}'
            WHEN 'time' THEN '${ColumnDataType.Time}'
            WHEN 'time without time zone' THEN '${ColumnDataType.Time}'
            WHEN 'timetz' THEN '${ColumnDataType.Time}'
            WHEN 'time with time zone' THEN '${ColumnDataType.Time}'
            WHEN 'timestamp' THEN '${ColumnDataType.Timestamp}'
            WHEN 'timestamp without time zone' THEN '${ColumnDataType.Timestamp}'
            WHEN 'timestamptz' THEN '${ColumnDataType.Timestamp}'
            WHEN 'timestamp with time zone' THEN '${ColumnDataType.Timestamp}'
            WHEN 'varbinary' THEN '${ColumnDataType.Binary}'
            WHEN 'binary varying' THEN '${ColumnDataType.Binary}'
            WHEN 'varbyte' THEN '${ColumnDataType.Binary}'
            ELSE '${ColumnDataType.String}'
          END AS type
        FROM cols
      )
      SELECT
        CONCAT('${this.tempLocation}.', table_name) AS fqn,
        '[' ||
          LISTAGG(
            '{"name":"' || name || '","type":"' || type || '","db_type":"' || db_type || '"}',
            ','
          )
          WITHIN GROUP (ORDER BY ordinal_position ASC)
        || ']'
        AS schema
      FROM cols_with_types
      GROUP BY table_name;`
  }

  prepareSchemaQueries(queries: Array<string>): Array<string> {
    const last = queries.pop() as string
    const procName = `_temp_${hashString(JSON.stringify(queries))}`
    return [
      dedent`
        CREATE OR REPLACE PROCEDURE ${procName}()
        AS $$
        BEGIN
          ${queries.join('\n')}
        EXCEPTION WHEN OTHERS THEN
        END;
        $$ LANGUAGE plpgsql;`,
      `CALL ${procName}();`,
      `DROP PROCEDURE ${procName}();`,
      last
    ]
  }

  getGeoDataType(): ColumnDataType {
    return ColumnDataType.Geometry
  }
}
